import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  DeleteSpaceMemberValidationSchema,
  type TDeleteSpaceMemberValidation,
  useDeleteSpaceMemberMutation,
} from '../../../api/delete-space-member.api';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { TOAST_MESSAGES_DELETE_SPACE_MEMBER } from './delete-space-member.constant';
import { useTicketingModalRootActions } from '@/features/ticketings/_shared/components/ticketing-modal-root/ticketing-modal-root.store';
import { GET_LIST_SPACE_MEMBER_QUERY_KEY } from '../../../api/get-list-space-member.api';

export interface IUseDeleteSpaceMemberModalProps {
  userId: string;
  spaceId: string;
  organizationId: string;
}

export function useDeleteSpaceMemberModal({ userId, spaceId, organizationId }: IUseDeleteSpaceMemberModalProps) {
  // HOOKS
  const showToast = useCustomToast();
  const modalActions = useTicketingModalRootActions();
  const form = useForm<TDeleteSpaceMemberValidation>({
    resolver: zodResolver(DeleteSpaceMemberValidationSchema),
    mode: 'onChange',
  });

  const mutationDeleteSpaceMember = useDeleteSpaceMemberMutation({
    mutationConfig: {
      onError: (_error) => {
        const errorMessage =
          axiosExtractErrorMessage(_error) || TOAST_MESSAGES_DELETE_SPACE_MEMBER.DELETE_SPACE_MEMBER_ERROR;
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: (_data) => {
        // Invalidate space member queries
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: [GET_LIST_SPACE_MEMBER_QUERY_KEY, { spaceId: spaceId || '' }],
          }),
        ]);
        modalActions.closeModal('DeleteSpaceMemberModal');
        form.reset();
        showToast({
          message: TOAST_MESSAGES_DELETE_SPACE_MEMBER.DELETE_SPACE_MEMBER_SUCCESS,
          type: 'success',
        });
      },
    },
  });

  // HELPERS
  const isOpenDeleteSpaceMemberModal = useMemo(() => {
    return modalActions.checkIsOpenModal('DeleteSpaceMemberModal');
  }, [modalActions]);

  const isDisabledForm = useMemo(() => {
    return mutationDeleteSpaceMember.isPending || form.formState.isSubmitting;
  }, [mutationDeleteSpaceMember.isPending, form.formState.isSubmitting]);

  const isLoadingForm = useMemo(() => {
    return mutationDeleteSpaceMember.isPending || form.formState.isSubmitting;
  }, [mutationDeleteSpaceMember.isPending, form.formState.isSubmitting]);

  // EFFECTS
  useEffect(() => {
    if (userId && spaceId && organizationId) {
      form.reset({
        userId: userId || '',
        spaceId: spaceId || '',
        organizationId: organizationId || '',
      });
    }
  }, [userId, spaceId, organizationId, form]);

  // HANDLERS
  const handleCloseModal = useCallback(() => {
    modalActions.closeModal('DeleteSpaceMemberModal');
  }, [modalActions]);

  const handleSubmitForm = useCallback(
    (data: TDeleteSpaceMemberValidation) => {
      if (data?.userId) {
        mutationDeleteSpaceMember.mutate({
          userId: data.userId,
          spaceId: data.spaceId,
          organizationId: data.organizationId,
        });
      }
    },
    [mutationDeleteSpaceMember]
  );

  const handleClose = useCallback(() => {
    handleCloseModal();
    form.reset();
  }, [handleCloseModal, form]);

  return {
    // Form
    form,

    // Modal
    isOpenDeleteSpaceMemberModal,

    // Helpers
    isDisabledForm,
    isLoadingForm,

    // Handlers
    handleCloseModal,
    handleSubmitForm,
    handleClose,
  };
}
